import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";

export default function MedicalPage() {
  const { t } = useLanguage();
  const [labs, setLabs] = useState([]);
  const [vitals, setVitals] = useState([]);
  const [meds, setMeds] = useState([]);
  const [allergies, setAllergies] = useState([]);
  const [conditions, setConditions] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    Promise.all([
      api.get("/zekaai/labs"),
      api.get("/zekaai/vitals"),
      api.get("/zekaai/medications"),
      api.get("/zekaai/allergies"),
      api.get("/zekaai/conditions"),
    ])
      .then(([l, v, m, a, c]) => {
        setLabs(l.data);
        setVitals(v.data);
        setMeds(m.data);
        setAllergies(a.data);
        setConditions(c.data);
      })
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }, []);

  return (
    <div className="space-y-8" data-testid="medical-page">
      <header>
        <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
          {t.layer.zekaai} · {t.layer.zekaaiSub}
        </div>
        <h1 className="mt-1 text-3xl font-semibold tracking-tight text-slate-900">
          {t.medical.title}
        </h1>
        <p className="mt-1 text-sm text-slate-500">{t.medical.sub}</p>
      </header>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
      )}

      <DataTable
        title={t.twin.labs}
        testid="medical-labs-table"
        columns={["name", "value", "unit", "ref_low", "ref_high", "status", "taken_at"]}
        rows={labs}
      />
      <DataTable
        title={t.twin.vitals}
        testid="medical-vitals-table"
        columns={["kind", "value", "unit", "taken_at", "source"]}
        rows={vitals}
      />
      <div className="grid gap-4 lg:grid-cols-3">
        <DataTable title={t.twin.medications} testid="medical-meds-table" columns={["name", "dose", "frequency", "active"]} rows={meds} />
        <DataTable title={t.twin.allergies} testid="medical-allergies-table" columns={["substance", "severity"]} rows={allergies} />
        <DataTable title={t.twin.conditions} testid="medical-conditions-table" columns={["name", "status"]} rows={conditions} />
      </div>
    </div>
  );
}

function DataTable({ title, columns, rows, testid }) {
  return (
    <div className="mios-card" data-testid={testid}>
      <div className="mios-card-header">
        <div className="mios-card-title">{title}</div>
        <div className="mios-card-sub">{rows.length}</div>
      </div>
      <div className="mios-card-body overflow-x-auto">
        {rows.length === 0 ? (
          <div className="text-sm text-slate-400">—</div>
        ) : (
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-[11px] uppercase tracking-[0.06em] text-slate-500">
                {columns.map((c) => (
                  <th key={c} className="py-2 pr-4 font-medium">{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, 25).map((r, i) => (
                <tr key={r.id || i} className="border-b border-slate-100 last:border-0">
                  {columns.map((c) => (
                    <td key={c} className="py-2 pr-4 align-top text-slate-700">
                      {formatCell(r[c])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function formatCell(v) {
  if (v == null) return "—";
  if (typeof v === "boolean") return v ? "✓" : "—";
  if (typeof v === "string" && v.includes("T") && v.length > 15) {
    try {
      return new Date(v).toLocaleString("tr-TR", { dateStyle: "medium", timeStyle: "short" });
    } catch {
      return v;
    }
  }
  return String(v);
}
